import { AuthManager, SESSION_KEY } from '../auth/AuthManager';
import { formatPhone } from '../contacts/phone';
import { CallWatcher, type EndedCall } from '../wda/callWatcher';
import { fetchOngoing, NoXBeesToken } from '../wda/clients';
import { onLogout, ready, reportAuthorized, showToast } from '../xbees/client';

const TOASTED_KEY = 'halo.toastedCalls';

/**
 * One toast per ended call the agent talked on (missed calls get none). Remembered in storage so a
 * reloaded background instance doesn't repeat it.
 */
export function toastEnded(ended: EndedCall[], toast: (msg: string) => Promise<void> = showToast, store: Pick<Storage, 'getItem' | 'setItem'> = localStorage): void {
  let seen: string[] = [];
  try {
    seen = JSON.parse(store.getItem(TOASTED_KEY) ?? '[]') as string[];
  } catch {
    seen = [];
  }
  for (const c of ended) {
    if (!c.talked || seen.includes(c.callId)) {
      console.info(`[halo] call ${c.callId} ended, no toast (talked=${c.talked}, already toasted=${seen.includes(c.callId)})`);
      continue;
    }
    seen = [c.callId, ...seen].slice(0, 50);
    console.info(`[halo] call ${c.callId} ended, showing toast`);
    toast(`Call with ${c.remoteName || formatPhone(c.remotePhone)} ended. Wrap it up in the HaloPSA tab.`).catch((e: unknown) =>
      console.warn('[halo] x-Bees toast failed', e),
    );
  }
  try {
    store.setItem(TOASTED_KEY, JSON.stringify(seen));
  } catch {
    // best effort
  }
}

/**
 * The always-running no-ui instance: the x-Bees handshake, then the WDA call watcher while the
 * agent is signed in to Halo (live calls → localStorage for the Info views).
 */
export async function startBackground(
  auth = new AuthManager(),
  watcher = new CallWatcher({
    fetchOngoing,
    store: localStorage,
    onEnded: (ended) => toastEnded(ended),
    onError: (e) => {
      if (e instanceof NoXBeesToken) {
        console.warn(e.message);
        watcher.stop(); // misconfiguration: polling won't fix it
      }
    },
  }),
): Promise<void> {
  await ready();
  let authorized: boolean;
  try {
    authorized = (await auth.checkSession()) !== null;
  } catch {
    authorized = auth.signedIn; // API down: keep the session
  }
  await reportAuthorized(authorized);

  const sync = (): void => (auth.signedIn ? watcher.start() : watcher.stop());
  sync();
  window.addEventListener('storage', (e) => {
    if (e.key === SESSION_KEY) sync();
  });
  auth.onSignedOut(() => watcher.stop());
  onLogout(() => void auth.signOut());
}
